import React, { useState, useEffect, useCallback } from "react";
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimesCircle, FaTimes } from "react-icons/fa";
import "./CustomAlert.css";

// Store for managing multiple alerts
let alertId = 0;

class AlertManager {
  constructor() {
    this.callbacks = [];
    this.alerts = [];
  }

  subscribe(callback) {
    this.callbacks.push(callback);
    return () => {
      this.callbacks = this.callbacks.filter((cb) => cb !== callback);
    };
  }

  notify(alerts) {
    this.alerts = alerts;
    this.callbacks.forEach((callback) => callback(alerts));
  }

  show(message, type = "info", duration = 4000) {
    const id = alertId++;
    const alert = { id, message, type, duration };
    this.alerts.push(alert);
    this.notify([...this.alerts]);

    if (duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }

    return id;
  }

  remove(id) {
    this.alerts = this.alerts.filter((alert) => alert.id !== id);
    this.notify([...this.alerts]);
  }

  removeAll() {
    this.alerts = [];
    this.notify([]);
  }
}

export const alertManager = new AlertManager();

// Hook to use alerts
export const useAlert = () => {
  return {
    success: (msg, duration) => alertManager.show(msg, "success", duration),
    error: (msg, duration) => alertManager.show(msg, "error", duration),
    warning: (msg, duration) => alertManager.show(msg, "warning", duration),
    info: (msg, duration) => alertManager.show(msg, "info", duration),
    remove: (id) => alertManager.remove(id),
    removeAll: () => alertManager.removeAll(),
  };
};

// Alert Container Component
export const AlertContainer = () => {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const unsubscribe = alertManager.subscribe(setAlerts);
    return unsubscribe;
  }, []);

  return (
    <div className="alert-container">
      {alerts.map((alert) => (
        <AlertNotification key={alert.id} alert={alert} />
      ))}
    </div>
  );
};

// Individual Alert Component
const AlertNotification = ({ alert }) => {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      alertManager.remove(alert.id);
    }, 300);
  };

  useEffect(() => {
    if (alert.duration > 0) {
      const timer = setTimeout(handleClose, alert.duration);
      return () => clearTimeout(timer);
    }
  }, [alert.duration]);

  const getIcon = () => {
    switch (alert.type) {
      case "success":
        return <FaCheckCircle className="alert-icon" />;
      case "error":
        return <FaTimesCircle className="alert-icon" />;
      case "warning":
        return <FaExclamationCircle className="alert-icon" />;
      case "info":
      default:
        return <FaInfoCircle className="alert-icon" />;
    }
  };

  return (
    <div
      className={`alert alert-${alert.type} ${isLeaving ? "alert-leaving" : ""}`}
      role="alert"
    >
      <div className="alert-content">
        {getIcon()}
        <p className="alert-message">{alert.message}</p>
      </div>
      <button
        className="alert-close-btn"
        onClick={handleClose}
        aria-label="Close alert"
      >
        <FaTimes size={14} />
      </button>
      <div className="alert-progress"></div>
    </div>
  );
};

// Modal Dialog Component for Confirmations
export const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  type = "warning",
  isDarkMode = true,
}) => {
  if (!isOpen) return null;

  return (
    <div className={`confirm-dialog-overlay ${isDarkMode ? "dark" : "light"}`}>
      <div className="confirm-dialog">
        <div className={`confirm-dialog-header confirm-dialog-${type}`}>
          <h2>{title}</h2>
        </div>
        <div className="confirm-dialog-body">
          <p>{message}</p>
        </div>
        <div className="confirm-dialog-footer">
          <button className="confirm-btn-cancel" onClick={onCancel}>
            {cancelText}
          </button>
          <button
            className={`confirm-btn-confirm confirm-btn-${type}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertContainer;
